import { hash32 } from '@gardener/core';
import { type PaletteKey, PixelCanvas } from '../lib';
import { grid, HEAD_X, HEAD_Y, type Part, type Recolor, stack } from './body';

/**
 * The look options' art (ADR-0073, ADR-0088): skin, hairstyle, hair colour, eyes, mouth, facial hair and extras, each
 * combining with every other. Authored in the default look's colours (peach skin `s S n`, chestnut hair `S m n N`); a
 * skin or hair colour is a recolour of its part, applied before the parts are composited (index.ts), so parts may share
 * colours. Stylish, never a caricature: the shapes are the hair's and the extras', open to every skin.
 *
 * Rubric of ADR-0088's options (looks sheet at 3× and 1:1, the picker, a 5v5 stage and game in the browser): skins
 * S5 R5 P4 L4 C5 F5 (umber on the palette's one skin-only tone); monolid, almond R4 C5; afro S5 R5 L4 C3 (its coils tile
 * evenly), braids S5 R4 C4 (apart, fanning out, a bead on each), locs S4 R4 C4 (thick, swaying, rounded ends), buzz S4
 * R4 (a crop the scalp shows through, a crisp line-up), top knot S5 R4, puffs S5 R5, sleek S4 R4 (strands, a sheen, a
 * pointed fringe); stubble R4, moustache, goatee R4, full R5; shades R5, glasses R5 (bold dark frames), earring R4 (a 7×8 hoop, its
 * shadow lifting it off the hair), chain R4. ADR-0090's beards (every hair colour on peach, bronze, umber and deep, 1:1
 * and in a 5v5): handlebar S5 R5 C4 in black or silver on any skin, R3 in chestnut or blonde on umber and deep (its dark
 * rim carries it); pencil R4 (two tones, one of them reads on every skin).
 */

/**
 * Skin ramps over the peach face: light `s`, shadow `S`, lines `n`, eyes and mouth `N`, blush `i`, and `t` the light
 * under a line feature (a happy eye's arc, a closed lid, a mouth): the skin itself on light skins, a step lighter on dark
 * ones, where a dark line alone sinks into the skin at 1:1. ENDESGA has six skin lights (e s t S m n; `N` swallows a
 * face's features), so fair and ivory share one (ivory's shadow and blush golden, not pink) and umber takes the palette's
 * one skin-only tone (`a`, between brown's `m` and deep's `n`): deep stays the darkest, its black eyes still read.
 */
export const SKINS = {
  peach: { t: 's' },
  fair: { s: 'e', S: 's', t: 'e' },
  tan: { s: 't', S: 'm' },
  brown: { s: 'm', S: 'n', n: 'N', i: 'u' },
  deep: { s: 'n', S: 'N', n: 'N', N: 'k', i: 'R', t: 'm' },
  ivory: { s: 'e', S: 't', i: 't', t: 'e' },
  bronze: { s: 'S', S: 'm', i: 'u', t: 's' },
  umber: { s: 'a', S: 'n', n: 'N', N: 'k', i: 'R', t: 'm' },
} as const satisfies Record<string, Recolor>;

/**
 * Hair colours over chestnut (sheen `S`, mid `m`, shadow `n`, deep `N`). Never the team hues (b B c, r R, y Y): black
 * leans slate and blonde ecru/tan so the shape still reads.
 */
export const HAIR_COLOURS = {
  chestnut: {},
  black: { S: '3', m: '2', n: '1', N: 'k' },
  blonde: { S: 'e', m: 't', n: 'm', N: 'n' },
  ginger: { S: 'o', m: 'U', n: 'u', N: 'n' },
  silver: { S: 'w', m: '5', n: '4', N: '3' },
  pink: { S: 'e', m: 'i', n: 'p', N: 'P' },
} as const satisfies Record<string, Recolor>;

/**
 * The face (36×20 at HEAD_X, HEAD_Y), facing right in 3/4: ear at the back (left), the cheek's shadow on the right,
 * blush under the eyes. The rows under the brim and the back of the head are covered by every hairstyle.
 */
export const FACE: Part = [
  grid(`
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    ssssssssssssssssssssssssssssssssssss
    sssssSSsssssssssssssssssssssssssssss
    ssssSssSsssssssssssssssssssssssssssS
    ssssSsnSsssssssssssssssssssssssssssS
    ssssSsnssssssssssssssssssssssssssssS
    sssssSSssssssssssssssssssssssssssssS
    ssssssSssssssssssssssssssssssssssssS
    ..ssssssssssssssssssssssssssssssssSS
    ....SssssssiiiissssssssssssssiiiissS
    ......SsssssiiiissssssssssssssiiiiS.
    .........SssssssssssssssssssssssSS..
    .............SSSssssssssssssSSSS....
  `),
  HEAD_X,
  HEAD_Y,
];

/**
 * The dead pose's head (32×24 at DEAD_AT), flat on his back beside the body: seen from above, the chin to the left
 * (by the shoulders) and the top of the head to the right, eyes shut. The look's hair lies over it (`Hair.dead`).
 */
export const DEAD_AT = [26, 45] as const;
export const DEAD_FACE: Part = [
  grid(`
    ...........Ssssssssssss.........
    ........Ssssssssssssssssss......
    ......Ssssssssssssssssssssss....
    ....Sssssssssssssssssssssssss...
    ...Sssssssssssssssssssssssssss..
    ..Sssssssssssssssssssssssssssss.
    ..Ssssiisssssssssssssssssssssss.
    .Sssssiissssssssssssssssssssssss
    .Sssssssssssssssssssssssssssssss
    Ssssssssssssssssssssssssssssssss
    Ssssssssssssssssssssssssssssssss
    Ssssssssssssssssssssssssssssssss
    Ssssssssssssssssssssssssssssssss
    Ssssssssssssssssssssssssssssssss
    Ssssssssssssssssssssssssssssssss
    .Sssssssssssssssssssssssssssssss
    .Sssssiissssssssssssssssssssssss
    ..SSSsiisssssssssssssssssssssss.
    ..SSSssssssssssssssssssssssssss.
    ...SSSssssssssssssssssssssssss..
    ....SSSSSSSSSSsssssssssssssss...
    ......SSSSSSSSssssssssssssss....
    ........SSSSSSssssssssssss......
    ...........SSSsssssssss.........
  `),
  ...DEAD_AT,
];

/** Eyes (20×8 at head (14, 8)): the near eye at x 2-5, the far one at x 14-17; each option open and blinking. */
const EYES_AT = [HEAD_X + 14, HEAD_Y + 8] as const;
const SHUT: Part = [
  grid(`
    ................................
    ................................
    ................................
    ................................
    ...........N....................
    ..........N.....................
    ..........N.....................
    ...........N....................
    ................................
    ................................
    ................................
    ................................
    ................................
    ................................
    ................................
    ................................
    ...........N....................
    ..........N.....................
    ..........N.....................
    ...........N....................
    ................................
    ................................
    ................................
    ................................
  `),
  ...DEAD_AT,
];
const SHUT_LASHES: Part = [
  grid(`
    ................................
    ................................
    ................................
    ............N...................
    ...........NN...................
    ..........N.....................
    ..........N.....................
    ...........N....................
    ................................
    ................................
    ................................
    ................................
    ................................
    ................................
    ................................
    ................................
    ...........N....................
    ..........N.....................
    ..........N.....................
    ...........NN...................
    ............N...................
    ................................
    ................................
    ................................
  `),
  ...DEAD_AT,
];
const eyes = (open: string, blink: string, dead = SHUT) => ({
  open: [grid(open), ...EYES_AT] as Part,
  blink: [grid(blink), ...EYES_AT] as Part,
  dead,
});
export const EYES = {
  round: eyes(
    `
    ....................
    ...kk.........kk....
    ..kwkk.......kwkk...
    ..kwkk.......kwkk...
    ..kkkk.......kkkk...
    ..kkkk.......kkkk...
    ..kNNk.......kNNk...
    ...kk.........kk....
  `,
    `
    ....................
    ....................
    ....................
    ....................
    ....................
    ..kkkk.......kkkk...
    ..tttt.......tttt...
    ....................
  `,
  ),
  lashes: eyes(
    `
    k..................k
    kkkkkk........kkkkkk
    ..kwkk........kwkk..
    ..kwkk........kwkk..
    ..kkkk........kkkk..
    ..kkkk........kkkk..
    ..kNNk........kNNk..
    ...kk..........kk...
  `,
    `
    ....................
    ....................
    ....................
    ....................
    ....................
    kkkkkk........kkkkkk
    ktktkt........tktktk
    ....................
  `,
    SHUT_LASHES,
  ),
  sleepy: eyes(
    `
    ....................
    ....................
    ....................
    ..SSSS.......SSSS...
    ..kkkk.......kkkk...
    ..kwkk.......kwkk...
    ..kNNk.......kNNk...
    ...kk.........kk....
  `,
    `
    ....................
    ....................
    ....................
    ..SSSS.......SSSS...
    ....................
    ....................
    ..kkkk.......kkkk...
    ..tttt.......tttt...
  `,
  ),
  happy: eyes(
    `
    ....................
    ....................
    ....................
    ...kk.........kk....
    ..kkkk.......kkkk...
    ..kttk.......kttk...
    ....................
    ....................
  `,
    `
    ....................
    ....................
    ....................
    ...kk.........kk....
    ..kkkk.......kkkk...
    ..kttk.......kttk...
    ....................
    ....................
  `,
  ),
  monolid: eyes(
    `
    ....................
    ....................
    ....................
    .kkkkk......kkkkk...
    .kwkkk......kwkkk...
    .kkkkk......kkkkk...
    .kkNkk......kkNkk...
    ..kkk........kkk....
  `,
    `
    ....................
    ....................
    ....................
    ....................
    ....................
    .kkkkk......kkkkk...
    ..ttt........ttt....
    ....................
  `,
  ),
  almond: eyes(
    `
    ....................
    ....................
    ....................
    ..kkkk.......kkkk...
    .kkwkkk.....kkwkkk..
    ..kkkkk.....kkkkk...
    ...kNk.......kNk....
    ....................
  `,
    `
    ....................
    ....................
    ....................
    ....................
    .k................k.
    ..kkkkk.....kkkkk...
    ...ttt.......ttt....
    ....................
  `,
  ),
} satisfies Record<string, { open: Part; blink: Part; dead: Part }>;

/** Mouths (7×3 at head (22, 16)), under the eyes' middle. */
const MOUTH_AT = [HEAD_X + 22, HEAD_Y + 16] as const;
const mouth = (src: string): Part => [grid(src), ...MOUTH_AT];

/** A mouth turned a quarter clockwise, for the dead pose's head (the top of the head to the right). */
export function turned([c]: Part): Part {
  const out = new PixelCanvas(c.h, c.w);
  for (let y = 0; y < c.h; y++) for (let x = 0; x < c.w; x++) out.set(c.h - 1 - y, x, c.get(x, y));
  return [out, DEAD_AT[0] + 2, DEAD_AT[1] + 8];
}
export const MOUTHS = {
  smile: mouth(`
    .N...N.
    ..NNN..
    ..ttt..
  `),
  grin: mouth(`
    .NNNNN.
    .NwwwN.
    ..NNN..
  `),
  cat: mouth(`
    .N.N.N.
    ..NtN..
    ..t.t..
  `),
  tongue: mouth(`
    .NNNNN.
    ..NiiN.
    ...ii..
  `),
} satisfies Record<string, Part>;

interface Hair {
  /** Over the face: the scalp under the brim, the back of the head, the fringe. */
  front: Part;
  /** Behind the head (long hair, braids, a bun; an afro or puffs under the brim, a knot behind the crown). */
  back: readonly Part[];
  /** Lying on the grass round the dead pose's head. */
  dead: Part;
}

/** Hair front grids sit at (8, 24): x = head x + 4. */
const FRONT_AT = [HEAD_X - 4, HEAD_Y] as const;
const front = (src: string): Part => [grid(src), ...FRONT_AT];
/** Lying hair grids sit 2 px left of and 6 px above the dead head. */
const deadHair = (src: string): Part => [grid(src), DEAD_AT[0] - 2, DEAD_AT[1] - 6];

/** The part's pixels where `keep` (frame x, y) answers. */
function clip([c, x0, y0]: Part, keep: (x: number, y: number) => boolean): Part {
  const out = new PixelCanvas(c.w, c.h);
  for (let y = 0; y < c.h; y++) for (let x = 0; x < c.w; x++) if (keep(x + x0, y + y0)) out.set(x, y, c.get(x, y));
  return [out, x0, y0];
}

/**
 * A round mass with a bumpy rim (an afro), in `m`: an ellipse round (cx, cy) in the frame, `bumps` round its rim, then
 * textured by `coils`.
 */
function puff(cx: number, cy: number, rx: number, ry: number, bumps: number): Part {
  const [x0, y0] = [Math.floor(cx - rx - 2), Math.floor(cy - ry - 2)];
  const c = new PixelCanvas(Math.ceil(rx * 2 + 5), Math.ceil(ry * 2 + 5));
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const dx = (x + x0 + 0.5 - cx) / rx;
      const dy = (y + y0 + 0.5 - cy) / ry;
      const edge = 1 + 0.06 * Math.cos(bumps * Math.atan2(dy, dx));
      if (Math.hypot(dx, dy) <= edge) c.set(x, y, 'm');
    }
  }
  return coils([c, x0, y0], cx, cy, rx, ry);
}

/**
 * Coils over a mass's `m` pixels: each a 3×3 ball lit from the top-left (sheen, mid, shadow), the rows of balls
 * offset like bricks; darker by a step past the middle of the mass (centre cx, cy, radii rx, ry) toward the bottom
 * right, and at its bottom rim.
 */
function coils([c, x0, y0]: Part, cx: number, cy: number, rx: number, ry: number): Part {
  const BALL = ['SSm', 'Smn', 'mnn'];
  const DARKER: Record<string, PaletteKey> = { S: 'm', m: 'n', n: 'N' };
  const out = c.clone();
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (c.get(x, y) !== 'm') continue;
      const [fx, fy] = [x + x0, y + y0];
      const row = Math.floor(fy / 3);
      let tone = BALL[fy % 3]![(fx + (hash32(row, 5) % 3)) % 3]! as PaletteKey;
      const lit = (fx - cx) / rx + (fy - cy) / ry;
      if (lit > 0.55 || c.get(x, y + 1) === null) tone = DARKER[tone] ?? tone;
      if (lit < -0.9 && tone === 'm') tone = 'S';
      out.set(x, y, tone);
    }
  }
  return [out, x0, y0];
}

/**
 * A strand: its left (or top, lying) edge where it starts, where it starts and ends along it, and how far it drifts
 * sideways by its end (braids fan out; negative: to the left, or up lying).
 */
type Strand = readonly [at: number, from: number, to: number, drift?: number];

/**
 * A rope of hair's cross-section, cycled down its length (`rows`; a neighbour `phase` rows further on), and its tip
 * (`ends`, the last rows: a bead, a rounded end); the last column is the deep line on its shadow side, `.` is bare.
 * `wave`: how far it sways side to side (locs are irregular), in px.
 */
interface Weave {
  rows: readonly string[];
  ends: readonly string[];
  phase: number;
  wave: number;
}
/** Box braids: thin 2-px plaits of regular links, a gold bead on every tip. */
const BRAID: Weave = { rows: ['SmN', 'mnN'], ends: ['mn.', 'YU.', 'UU.'], phase: 1, wave: 0 };
/** Locs: thick 4-px ropes, lumpy (a pinch every few rows, out of step with their neighbours), swaying, rounded ends. */
const LOC: Weave = {
  rows: ['SmmnN', 'SmmnN', 'mSmnN', 'nmnnN', 'SmmnN'],
  ends: ['SmmnN', 'mmmn.', '.mn..'],
  phase: 3,
  wave: 1,
};

/**
 * Ropes of hair (braids, locs): each strand's weave down its length, drifting and swaying, a step darker over its
 * last third. `lying`: along x (the dead pose), strands given as [y, x0, x1, drift].
 */
function ropes(weave: Weave, strands: readonly Strand[], lying = false): Part {
  const DARKER: Record<string, PaletteKey> = { S: 'm', m: 'n', n: 'N' };
  const w = weave.rows[0]!.length;
  const reach = weave.wave + Math.max(0, ...strands.map(([, , , d = 0]) => Math.abs(d)));
  const x0 = Math.min(...strands.map(([a]) => a)) - reach;
  const y0 = Math.min(...strands.map(([, f]) => f));
  const x1 = Math.max(...strands.map(([a]) => a)) + w + reach;
  const y1 = Math.max(...strands.map(([, , t]) => t)) + 1;
  const c = new PixelCanvas(x1 - x0, y1 - y0);
  strands.forEach(([a, from, to, drift = 0], i) => {
    for (let v = from; v <= to; v++) {
      const along = (v - from) / Math.max(1, to - from);
      const sway = Math.round(Math.sin((v + i * 5) / 3.5) * weave.wave);
      const dx = Math.round(drift * along) + sway;
      const tip = weave.ends.length - 1 - (to - v);
      const row = tip >= 0 ? weave.ends[tip]! : weave.rows[(v + i * weave.phase) % weave.rows.length]!;
      for (let d = 0; d < w; d++) {
        if (row[d] === '.') continue;
        let tone = row[d] as PaletteKey;
        if (along > 0.65) tone = DARKER[tone] ?? tone;
        c.set(a + dx + d - x0, v - y0, tone);
      }
    }
  });
  if (!lying) return [c, x0, y0];
  const t = new PixelCanvas(c.h, c.w);
  for (let y = 0; y < c.h; y++) for (let x = 0; x < c.w; x++) t.set(y, x, c.get(x, y));
  return [t, y0, x0];
}

/**
 * A close crop: the `m` pixels thinned to every other one (the scalp shows through, a shadow over it), the drawn line-up
 * (`n`) left solid.
 */
function cropped([c, x0, y0]: Part): Part {
  const out = c.clone();
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++) if (c.get(x, y) === 'm') out.set(x, y, (x + x0 + y + y0) % 2 ? null : 'n');
  return [out, x0, y0];
}

/** Short hair's speckle over the `m` pixels: a few shadow dots, and sheen ones above row `lit`. */
function speckle([c, x0, y0]: Part, lit: number): Part {
  const out = c.clone();
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (c.get(x, y) !== 'm') continue;
      const h = hash32(x + x0, y + y0, 11) % 7;
      if (h === 0) out.set(x, y, 'n');
      else if (h === 1 && y + y0 < lit) out.set(x, y, 'S');
    }
  }
  return [out, x0, y0];
}

/**
 * A cap of short hair on the dead pose's head: its pixels (and a ring 1 px out) past a hairline that curves round
 * x `at`, speckled, `combed` back (a comb line every third row, the sheen along the top) or in `coils`.
 */
function deadCap(at: number, texture: 'speckle' | 'combed' | 'coils' = 'speckle'): Part {
  const [f, fx, fy] = DEAD_FACE;
  const c = new PixelCanvas(f.w + 2, f.h + 2);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const [ax, ay] = [x - 1 + fx, y - 1 + fy];
      const near = [0, -1, 1].some((dx) => [0, -1, 1].some((dy) => f.get(x - 1 + dx, y - 1 + dy) !== null));
      if (!near || ax < at + ((ay - 56.5) / 11.5) ** 2 * 3) continue;
      c.set(x, y, texture !== 'combed' ? 'm' : ay % 3 === 0 ? 'n' : ay < 50 ? 'S' : 'm');
    }
  }
  const cap: Part = [c, fx - 1, fy - 1];
  return texture === 'speckle' ? speckle(cap, 52) : texture === 'coils' ? coils(cap, 50, 56, 8, 12) : cap;
}

/** The afro's whole mass, behind the head (its front over the back of the head and the forehead: `HAIRS.afro`). */
const AFRO = puff(30, 29, 27, 16, 9);

export const HAIRS = {
  /** Bob (the first look): three locks of fringe, the sides over the ears curling in at the jaw. */
  bob: {
    front: front(`
      .....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn....
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
      ...mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ..mmmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ..mmmnnnnnnnnNnnnnnnnNnnnnnnnnnnnNnnnnnnnn..
      .mmmmmnmmmnmmnSSmmmnnNmSSSmmmmnnnNmSSmmmnn..
      .mSSSSnSSSnmmnmSmmmmnnmmSSmmmmmnnnmmSmmmnn..
      .mmSSmnmSSnSmnmmmmmnm.mmmmmmmnnmm.mmmmmnnm..
      .mmmmmnmmmnSnmmmmmnm.....mmmnm......mmmnnm..
      .mmmmnmmmnmnnmmmmnm.......mm..........mmn...
      .mmmmnmmmnmnnmmmmm.....................mm...
      .mmmmnmmmnmnnmmm............................
      .mmmnmmmnmmnnmm.............................
      ..nnnmmmnmmnnn..............................
      ..nnnmmmnmmnnm..............................
      ..mnnnnnnnnnn...............................
      ...mnnnNnnnn................................
      ....mnNnnNn.................................
    `),
    back: [],
    dead: deadHair(`
      ......................................
      ......................................
      ......................................
      ......................................
      ......................................
      ..............nnnnnnnmmm..............
      ............mmmmmmmmmnnnnn............
      .........mmmmmmmSSSSSmmmmmmmm.........
      .....nnnmmmmmmSSSSmmmmmmmmmmmmm.......
      .....mmmnnnnnmmmmmSSmmmmmmmmmmmm......
      ...............nnnnnnmmmmmmmmmmmm.....
      ................mmmmmnnnnnnnnnnnmm....
      .................mmmmmmmmmmmmmmmmm....
      .................mmmmmmmmmmmmmmmmmm...
      ..................mmmmmmmmmmmmmmmmm...
      ..................nnnmmmmmmmmmmmmmm...
      ..................mmmnnnnnnnnnnnmmm...
      ..................mmmmmmmmmmmmmmmmm...
      ..................mmmmmmmmmmmmmmmmm...
      ..................mmmmmmmmmmmmmmmmm...
      ..................mmmmmmmmmmmmmmmmm...
      ..................mmmnnnnnnnnnnnmmm...
      .................nnnnmmmmmmmmmmmmmm...
      .................mmmmmmmmmmmmmmmmm....
      ................nnnnnnnnnnnnnnnnnn....
      ...............nnnnnnnnnnnnnnnnnn.....
      .....mmmmmmmmmmnnnnnnNNNNNNNNNNN......
      .....nnnnnnnnNNNNNNNNnnnnnnnnnn.......
      .........NNNNnnnnnnnnnnnnnnnn.........
      ............nnnnnnnnnnnnnn............
      ..............nnnnnnnnnn..............
      ......................................
    `),
  },
  /** Short: tight back and a sideburn before the ear (it shows), a short fringe swept forward in spikes, a quiff. */
  short: {
    front: front(`
      .....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ...mmmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmm.
      ...mmmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmm.
      ..mmmmmnmmnmmmnmSSmmnmSSmmnmSSmnmSSmmnSSmSm.
      ..mSSSmnSSnmmnnmmmmmmmmmmmmmmmmmmmmmmmmmnm..
      ..mmSSmnSmnmnmmm..mn...mnm...mn...mmn..mn...
      ..mmmmnmmnmmnmm....m....mn....m....mn.......
      ..mmmmnmnnnnnnm..........m..........m.......
      ..mnmmnm....mn..............................
      ...nmmmm.....n..............................
      ...mnnnn.....n..............................
      ....nnnn....................................
    `),
    back: [],
    dead: deadHair(`
      ......................................
      ......................................
      ......................................
      ......................................
      ......................................
      ......................................
      ..............mmmmmmmnnnn.............
      ...........mmmmmmSSSSmmmmmmm..........
      .........mmmmmmmSSSmmmmmmmmmmm........
      ...............mmmmmmmmmmmmmmmm.......
      ................nnnnnmmmmmmmmmmm......
      .................mmmmnnnnnnnnnnnm.....
      ...................mmmmmmmmmmmmmm.....
      ..................mmmmmmmmmmmmmmmm....
      ...................mmmmmmmmmmmmmmm....
      ....................nmmmmmmmmmmmmm....
      ...................mmnnnnnnnnnnnmm....
      ...................mmmmmmmmmmmmmmm....
      ....................mmmmmmmmmmmmmm....
      ...................mmmmmmmmmmmmmmm....
      ...................mmmmmmmmmmmmmmm....
      ....................mnnnnnnnnnnnmm....
      ..................nnnmmmmmmmmmmmmm....
      ..................mmmmmmmmmmmmmmm.....
      ..................nnnnnnnnnnnnnnn.....
      ................nnnnnnnnnnnnnnnn......
      ..............mnnnnnnNNNNNNNNNN.......
      .........nnnnNNNNNNNNnnnnnnnnn........
      ...........NNnnnnnnnnnnnnnnn..........
      ..............nnnnnnnnnnn.............
      ......................................
      ......................................
    `),
  },
  /** Pigtails: blunt bangs, the sides combed back to two tails tied low; the far one peeks out beside the cheek. */
  pigtails: {
    front: front(`
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn....
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
      ...mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ..mmmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ..mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ..mmmmmnSSmnmmmmnSSmnmSSSmnmSSSmnmSSSmnmmm..
      .mSSSSmnSmmnmmmmnmSmnnmSmmnnmSmmnnmSmmnmmm..
      .mmSSmnmmmnmmmmnmmmmm.mmmmm.mmmmm.mmmmnmmm..
      .mmmmmnmmmnmmmmnm.....................mmmm..
      .mmmmnmmmnmmnnmm.......................mm...
      .mmmmnmmmnmmnnm........................mm...
      .mnnmmmmnmmmnnm.............................
      ..nnmmmmmmmmnm..............................
      ..mnnnnnnnnnnm..............................
      ...mnnnnnnnnn...............................
    `),
    back: [
      [
        grid(`
        ......mm.
        .....NNNN
        ....mmNNN
        ....SmNNm
        ...Smmnm.
        ...Smnnm.
        ...mmnnm.
        ..Smnnm..
        ..mmnnm..
        ..mnnmm..
        .Smnnm...
        .mnnmm...
        .mnnmm...
        mmmnm....
        mmnmm....
        nmnm.....
        mnmm.....
        .nm......
      `),
        4,
        34,
      ],
      [
        grid(`
        nn........
        nnm.......
        nmmm......
        mnmmS.....
        mnmmmS....
        mmnmmS....
        mmnmmmS...
        mmmnmmS...
        mmmnmmm...
        mmmmnmmS..
        mmmmnmmm..
        .mmmmnmm..
        .mnmmnmmm.
        .mmnmmnmm.
        ..mmmmnmm.
        ..mmnmmnm.
        ...mmmmnmm
        ....mmmmnm
        ....mmnmnm
        .....mmnmn
        ......mmnn
        .......mn.
      `),
        46,
        33,
      ],
    ],
    dead: deadHair(`
      .................................mn...
      ...............................mmn....
      .............................mmSn.....
      ...........................mmSS.......
      ..........................mSSn........
      .........................NNm..........
      .............mmmmmmmmnnnn.............
      ..........mmmmmmSSSSSmmmmmmm..........
      .........mmmmmSSSSmmmmmmmmmmmm........
      .......mnnnnnmmmmmmmmmmmmmmmmmm.......
      ...............nnnnnnmmmmmmmmmmm......
      ................mmmmmnnnnnnnnnnnm.....
      .................mmmmmmmmmmmmmmmm.....
      .................mmmmmmmmmmmmmmmmm....
      ..................mmmmmmmmmmmmmmmm....
      ..................nnnmmmmmmmmmmmmm....
      ..................mmmnnnnnnnnnnnmm....
      ..................mmmmmmmmmmmmmmmm....
      ..................mmmmmmmmmmmmmmmm....
      ..................mmmmmmmmmmmmmmmm....
      ..................mmmmmmmmmmmmmmmm....
      ..................mmmnnnnnnnnnnnmm....
      .................nnnnmmmmmmmmmmmmm....
      .................mmmmmmmmmmmmmmmm.....
      ................nnnnnnnnnnnnnnnnn.....
      ...............nnnnnnnnnnnnnnnnn......
      .......mmmmmmmmnnnnnnNNNNNNNNNN....mmn
      .........nnnnNNNNNNNNnnnnnnnnn...mmmnn
      ..........NNNnnnnnnnnnnnnnNN..mmSSnn..
      .............nnnnnnnnnnnn.NNmSSSn.....
      ......................................
      ......................................
    `),
  },
  /** Long: a side-swept fringe, a lock down the far cheek, straight hair down the back to the hips. */
  long: {
    front: front(`
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn....
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
      ...mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
      ..mmmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmm..
      ..mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmm..
      .mmmmmnmmmnmmmnnnSSSSSmnnSSSSSSmSSmmnmmmmm..
      .mmmmmnmmmnmmmmmmnnnnmSSSmmm.......mmnmmSm..
      .mSSSSnSSSnmmmmmmmmmmmmm.............mnmSm..
      .mmSSmnmSSnmnnmmnnn...................nmSm..
      .mmmmmnmmmnmnnmnm.....................mnmm..
      .mmmmmnmmmnmnnnm.......................nmm..
      .mmmmmnmmmnmnnm........................nmm..
      .mmmmmnmmmnmnnm........................nmm..
      .mmmmmnmmmnmnn.........................mn...
      .mmmmmmmmmnnnm..........................m...
      .mmmmmmmmmmnnm..............................
      .mmmmmmmmmmmm...............................
      .mmmmmmmmmmm................................
    `),
    back: [
      [
        grid(`
        ...mmmmmmmmmmmm
        ...mmmmmmmmmmmm
        ..mmmmmmmmmmmmm
        ..mmmmmmmmmmmmm
        .mmmmmmmmmmnnnn
        .mmmmmmmmmmmnnn
        .mmmnmmmnmmmnnn
        .mmmnmmmnmmmnnn
        mSmmnmmmnmmmnnn
        mSmmnmmmnmmmnnn
        mSmmnmmmnmmmnnn
        mmSmmnmmnmmmnnn
        mmSmmnmmnmmmnnn
        mmSmmnmmnmmmnnn
        mSmmmnmmnmmmnnm
        mSmmmnmmmnmmnnm
        mmmmmnmmmnmnnn.
        mmmmmnmmmnmnnm.
        mmmmnmmmmnmnnm.
        mmmmnmmmmnmnm..
        mmmmnmmmnmnnm..
        .mmmnmmmnmnm...
        .mmmmmmmnmnm...
        .mmmmmnmmmm....
        .nnmmmnmmm.....
        ..mmmmmn.m.....
        ..mnnm.m.......
        ...m.m.........
      `),
        3,
        30,
      ],
    ],
    dead: deadHair(`
      ......................................
      ......................................
      ......................................
      ......................................
      ..............mmmmmmmmmmmm............
      ..........nmmnnnSSSSSmmmmmmmmm........
      ...........mmmSSSSmmmnnnnnnnn.........
      ........mmmmSSSmmmmmmmmmmmmmmmmm......
      ....nnnnmmmmmmmmmmSSSmmmmmmmmmmmmm....
      ....mmmmnnnnnmmmmmmmmmmmmmmmmmmmmmm...
      ...............nnnnnnmmmmmmmmmmmmmmm..
      ................mmmmmnnnnnnnnnnnmmmmn.
      .................mmmmmmmmmmmmmmmmmmmm.
      .................mmmmmmmmmmmmmmmmmmmmm
      ..................mmmmmmmmmmmmmmmmmmmm
      ..................nnnmmmmmmmmmmmmmmmmm
      ..................mmmnnnnnnnnnnnmmmmmm
      ..................mmmmmmmmmmmmmmmmmmmn
      ..................mmmmmmmmmmmmmmmmmmmm
      ..................mmmmmmmmmmmmmmmmmmmm
      ..................mmmmmmmmmmmmmmmmmmmm
      ..................mmmnnnnnnnnnnnmmmmmm
      .................nnnnmmmmmmmmmmmmmmmmm
      .................mmmmmmmmmmmmmmmmmmmn.
      ................nnnnnnnnnnnnnnnnnnnnn.
      ...............nnnnnnnnnnnnnnnnnnnnn..
      ....mmmmmmmmmmmnnnnnnNNNNNNNNNNNnnn...
      ....nnnnnnnnnNNNNNNNNnnnnnnnnnnnnn....
      ........NNNNNnnnnnnnnnnnnnnnnnnn......
      ...........nnnnnnnnnnnnnnnnnn.........
      ........nnnnnnnnnnnnnnnnnnnnnnnnn.....
      ......................................
    `),
  },
  /** Bun: combed back from a neat hairline, one wisp at the temple, a round bun at the back of the head. */
  bun: {
    front: front(`
      .....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn....
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
      ...mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ...mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ..mmmmmmmmmmmmmnmSSSmnmSSSmnmSSSmnmSSSmnmm..
      ..mSSSmmmnSSmmmmnmmmmmnmmmmmnmmmmmnmmmmmmm..
      ..mmSSmmnmSmmnmm.....................mmmm...
      ..mmmmmnmmmmnmm.......................nmm...
      ..mmmmnmmmmnmm........................mnm...
      ..mmmnmmmmmmnm.........................nm...
      ...nnmmmmmmmn..........................m....
      ...nmnnnnnnn................................
      ....nnnnnnnn................................
    `),
    back: [
      [
        grid(`
        ...mmmmm....
        ..mSSSnmmm..
        .mSSmmmnmmm.
        .SSmmmmmnmN.
        mSmmmnSSnmNN
        mSmmnNNmnmNN
        mmmmnmmnmnNN
        mmnmmnnmmnNN
        mmnmmmmmmnNN
        .mmnmmmmnmNm
        .mmnnnnnmmm.
        ..mmmnnnnm..
        ...mmmmm....
      `),
        3,
        32,
      ],
    ],
    dead: deadHair(`
      ......................................
      ......................................
      ......................................
      ......................................
      ......................................
      ......................................
      ..............mmmmmmmnnnn.............
      ...........mmmmmmSSSSmmmmmmm..........
      .........mmmmmmmSSSmmmmmmmmmmm........
      ..............mmmmmmmmmmmmmmmmm.......
      ................nnnnnmmmmmmmmmmm......
      .................mmmmnnnnnnnnnnnm.....
      ..................mmmmmmmmmmmmmmm.....
      ..................mmmmmmmmmmmmmmmmm...
      ...................mmmmmmmmmmmmSSmmmm.
      ...................nnmmmmmmmNmSSmnmmmm
      ...................mmnnnnnnnNmSmmmnmmm
      ...................mmmmmmmmmNmmnmmnmmm
      ...................mmmmmmmmmNmmmnnmmnm
      ...................mmmmmmmmmNmmmmmmnmm
      ...................mmmmmmmmmNmmmmmnnmm
      ...................mmnnnnnnnnNmmnnnnm.
      ..................nnnmmmmmmmmmmnnnn...
      ..................mmmmmmmmmmmmmmm.....
      .................nnnnnnnnnnnnnnnn.....
      ................nnnnnnnnnnnnnnnn......
      ..............mnnnnnnNNNNNNNNNN.......
      .........nnnnNNNNNNNNnnnnnnnnn........
      ...........NNnnnnnnnnnnnnnnn..........
      ..............nnnnnnnnnnn.............
      ......................................
      ......................................
    `),
  },
  /** Curly: round curls, each with a lit top and a dark heart, and more volume round the head. */
  curly: {
    front: front(`
      ...nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ...nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ...nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ..SSmnSSmmmnnnnSSmnnnnnnnSSmnnnnnnnSSmnnnn..
      .SSmmmSmNNmnnnSSmmmnSSmnSSmmmnSSmnSSmmmnnn..
      .SmNNmmmNmnn..SmNNmSSmmmSmNNmSSmmmSmNNmSSm..
      .mmNmnnmnnnSSmmmNmnSmNNmmmNmnSmNNmmmNmSSmmm.
      ..mnnnSSmnSSmmmmnnnmmNmnnmnnnmmNmnnmnnSmNNmn
      .SSmnSSmmmSmNNmnnn..mnnn..nn..mnnn..nnmmNmnn
      SSmmmSmNNmmmNmnn.....nn........nn......mnnn.
      SmNNmmmNmnnmnnn.........................nn..
      mmNmnnmnnn..SSm.............................
      .mnnn..SSm.SSmmm............................
      ..SSm.SSmmmSmNNmn...........................
      .SSmmmSmNNmmmNmnn...........................
      .SmNNmmmNmnnmnnn............................
      .mmNmSSmnnn..nn.............................
      ..mnSSmmmn..................................
      ...nSmNNmn..................................
      ....mmNmnn..................................
    `),
    back: [],
    dead: deadHair(`
      ......................................
      ......................................
      ......................................
      ......................................
      ..............mmmmmmmmmm..............
      ...........mmmmSSmmmmSSmmmm...........
      ...........mmmSmNmnmSmNmnmm...........
      ........mmmmmmmNmnnmmNmnnmSmNm........
      ....mmmmmmmmSSmnnnmmmnnnmmmNmnnm......
      .....mmmmmmSmNmnmmSSmmmmSSmnnnSSm.....
      ...............nmSmNmnmSmNmnmSmNmn....
      ................mmNmnnmmNmnnmmNmnnm...
      .................mnnnmmmnnnmmmnnnmm...
      .................SmmmmSSmmmmSSmmmmmm..
      ..................mnmSmNmnmSmNmnmSSm..
      ..................nnmmNmnnmmNmnnSmNm..
      ..................nmmmnnnmmmnnnmmNmn..
      ..................SSmmmmSSmmmmSSmnnn..
      ..................mNmnmSmNmnmSmNmnSS..
      ..................NmnnmmNmnnmmNmnSmN..
      ..................nnnmmmnnnmmmnnnmNm..
      ..................mmmmSSmmmmSSmmmmnn..
      .................NmnmSmNmnmSmNmnmSSm..
      .................mnnmmNmnnmmNmnnSmN...
      ................nnnmmmnnnmmmnnnmmNm...
      ...............mmmSSmmmmSSmmmmSSmn....
      .....mmmmmmSmNmnmSmNmnmSmNmnmSmNm.....
      ....mmmmmmmmNmnnmmNmnnmmNmnnmmNm......
      ........mmmmnnnSSmnnnSSmnnnSSm........
      ...........mmmSmNmnmSmNmnmS...........
      ...........mmmmNmnnmmNmnnmmN..........
      ...............nnnmmmnn...............
    `),
  },
  /**
   * Afro: a round mass of coils wider than the head, the hat sitting in it: it puffs out under the brim on both sides
   * and behind; a soft hairline across the forehead. Lying, a halo round the head.
   */
  afro: {
    front: clip(AFRO, (x, y) => x <= 21 - Math.max(0, y - 31) * 0.8 || y <= 28 || (y === 29 && x >= 25 && x <= 39)),
    back: [AFRO],
    dead: clip(puff(46, 57, 14, 12, 9), (x, y) => x >= 43 || ((x - 42) / 15) ** 2 + ((y - 56.5) / 11) ** 2 > 1),
  },
  /**
   * Box braids: parted into boxes along a neat hairline, thin braids over the back of the head and down the back to the
   * hips, two behind the far shoulder. Lying, fanned out past the head.
   */
  braids: {
    front: stack(
      front(`
        .....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
        ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn.
        ....mnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmmm.
        ....mSmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmmSm.
        ....mmSmmnmmmmmNmmmmmNmmmmNmmmmmNmmmmmNmmmn.
      `),
      ropes(BRAID, [
        [9, 25, 43, -1],
        [12, 25, 47],
        [15, 25, 44],
        [18, 25, 40],
      ]),
    ),
    back: [
      ropes(BRAID, [
        [5, 28, 55, -3],
        [9, 27, 61, -2],
        [13, 27, 58, -1],
        [17, 28, 62],
        [45, 29, 48, 1],
        [49, 30, 53, 2],
      ]),
    ],
    dead: ropes(
      BRAID,
      [
        [44, 42, 57, -2],
        [48, 42, 61, -1],
        [52, 42, 59],
        [56, 42, 61],
        [60, 42, 58, 1],
        [64, 43, 57, 2],
      ],
      true,
    ),
  },
  /** Locs: thick rounded ropes from a neat hairline, past the jaw to the shoulders. Lying, spread past the head. */
  locs: {
    front: stack(
      front(`
        .....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
        ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn.
        ....mnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmmm.
        ....mSmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnmmSm.
        ....mmSmmmnmmmmmmnmmmmmmnmmmmmmnmmmmmmnmmmn.
      `),
      ropes(LOC, [
        [8, 25, 42],
        [12, 25, 46],
        [16, 25, 40],
      ]),
    ),
    back: [
      ropes(LOC, [
        [3, 29, 46, -1],
        [9, 27, 51],
        [15, 28, 48],
        [44, 29, 44, 1],
        [50, 30, 41],
      ]),
    ],
    dead: ropes(
      LOC,
      [
        [43, 42, 57, -1],
        [49, 42, 61],
        [55, 42, 59],
        [61, 43, 56, 1],
      ],
      true,
    ),
  },
  /** Buzz cut: a close crop, the scalp showing through, a crisp line-up at the forehead and temple, the ear clear. */
  buzz: {
    front: cropped(
      front(`
        .....mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm...
        ....mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm...
        ...nmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmn...
        ...nmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmn...
        ...nmmmmmmmmmnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
        ...nmmmmmmmmmn..............................
        ...nmmmmmmmmmn..............................
        ...nmmmmmmmmmn..............................
        ...nmmmnnnnnnn..............................
        ...nmmmn....nn..............................
        ...nmmmn....nn..............................
        ...nmmn.....n...............................
        ...nmn......................................
        ....n.......................................
      `),
    ),
    back: [],
    dead: deadCap(42),
  },
  /** Top knot: slicked back with comb lines, tied in a knot on the crown that peeks out behind the hat. */
  topknot: {
    front: front(`
      .....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn....
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn...
      ...mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ...mSSmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn.
      ..mSSmmnmmmmSSSmnmmmSSSmnmmmSSSmnmmmSSSmmm..
      ..mmSmmnmmmn................................
      ..mmmSmmnmn.................................
      ..mmmmSmmn..................................
      ..nmmmmSmn..................................
      ...nmmmmn...................................
      ...nnmmn....................................
      ....nnn.....................................
    `),
    back: [
      [
        grid(`
          ..mmmm..
          .mSSmmn.
          mSSmmmnN
          mSmmmnnN
          mmmmnnNN
          .mmnnNN.
          ..NNNN..
          ..nmmn..
          .nmmmmn.
        `),
        10,
        5,
      ],
    ],
    dead: stack(deadCap(42, 'combed'), [
      grid(`
        .Nmm..
        NNSSm.
        NNSmmn
        NNmmnn
        NNmnnn
        NNmnn.
        .Nnn..
      `),
      56,
      53,
    ]),
  },
  /**
   * Afro puffs: coils pulled up from a soft hairline into two round puffs that peek out under the brim on either side,
   * like pigtails but coily. Lying, a puff either side of the crown.
   */
  puffs: {
    front: coils(
      front(`
        .....mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm..
        ....mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm..
        ...mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm..
        ...mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm..
        ...mmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmm..
        ...mmmmmmmmmmmm.............................
        ...mmmmmmmmmmm..............................
        ...mmmmmmmmm................................
        ...mmmmmmm..................................
        ....mmmm....................................
      `),
      30,
      27,
      22,
      6,
    ),
    back: [puff(9, 34, 7, 7, 7), puff(54, 32, 7, 7, 7)],
    dead: stack(deadCap(44, 'coils'), puff(55, 44, 5, 5, 6), puff(56, 65, 5, 4, 6)),
  },
  /** Sleek: straight and glossy, heavy blunt bangs to the brows, the sides cut straight at the jaw. */
  sleek: {
    front: front(`
      ....nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..
      ...nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn.
      ..mmmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn
      ..mSmnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn
      .SmnSmnmmnnnmmmnmmmnmmmnmmmnmmmnmmmnmmmnmSmn
      .SmnSmnmmnnnSSmnSSmnSSmnSSmnSSmnSSmnSSmnmSmn
      .SmnSmnmmnmnmmmnmmmnmmmnmmmnmmmnmmmnmmmnmSmn
      .Smnmmnmmnn.nmn.nmn.nmn.nmn.nmn.nmn.nmn.mmmn
      .Smnmmnmmnm..n...n...n...n...n...n...n...mmn
      .Smnmmnmmnm..............................mmn
      .Smnmmnmmnm..............................mmn
      .Smnmmnmmnm..............................mmn
      ..mnmmnmmnm..............................mm.
      ..mnmmnmmn...............................nn.
      ..mnmmnmmn................................n.
      ...nmmnmmn..................................
      ...nmnnmm...................................
      ....mm.mm...................................
      .....m.m....................................
      ......n.....................................
    `),
    back: [
      [
        grid(`
          ...mmnmmnm
          ..nSmnmmnm
          .mnSmnmmnm
          .mnSmnmmnm
          mmnSmnmmnm
          mmnSmnmmnm
          mmnSmnmmnm
          mmnSmnmmnm
          mmnSmnmmnm
          mmnmmnmmnm
          mmnmmnmmnm
          .mnmmnmmnm
          .mnmmnmmn.
          .mnmmnmmn.
          ..nmmnmm..
          ..nm.nmm..
          ...m.nm...
          .....n....
        `),
        5,
        29,
      ],
    ],
    dead: deadHair(`
      ......................................
      ......................................
      ......................................
      ......................................
      .......................mmmmmmmmmmm....
      .....................mmmSSSSSSmmmmmm..
      ...................nmmmmmmmmmmmmmmmmm.
      ..................nmmmmmmmmmmmmmmmmmmm
      .................nmmmmnnnnnnnnnnnnnnnn
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmnnnnnnnnnnnnnnnn
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmnnnnnnnnnnnnnnnn
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmnnnnnnnnnnnnnnnn
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmnnnnnnnnnnnnnnnn
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmmmmmmmmmmmmmmmmm
      ................nmSmmmnnnnnnnnnnnnnnnn
      .................nmmmmmmmmmmmmmmmmmmmm
      ..................nmmmmmmmmmmmmmmmmmmn
      ...................nnnnnnnnnnnnnnnnnn.
      .....................NNNNNNNNNNNNNNN..
      .......................nnnnnnnnnnn....
      ......................................
      ......................................
      ......................................
    `),
  },
} satisfies Record<string, Hair>;

interface Beard {
  /** On the face, under the hair: never below the mouth's top row, so every mouth shows. */
  front: readonly Part[];
  /** On the dead pose's head. */
  dead: readonly Part[];
}

/** Beard grids sit at the face's row 9 (the eyes' top): x = head x. */
const beard = (src: string): Part => [grid(src), HEAD_X, HEAD_Y + 9];
/** Lying beard grids sit at the dead head's top-left. */
const deadBeard = (src: string): Part => [grid(src), ...DEAD_AT];

const FULL = beard(`
  ........Sm..........................
  ........Smm.........................
  ........Smm.........................
  ........Smmn........................
  ........SmmmN.......................
  .......SmmmmmN......................
  ......SmmmmmmmN.....nmmSSmmn.....Nn.
  .....SmmmmmmmmmmN..nmmmmmmmmmn.nmmmN
  ......mmmmmmmmmmmmmnm.......nmmmmmmN
  ........mmmmmmmmmmmmmN.....Nmmmmmmm.
  ............mmmmmmmmmmmnnnnnmmmmmm..
  ..............nmmmmmmmmmmmmmmmmmn...
  .................NnnnnnnnnnnnnnN....
`);
/** The full beard lying: round the chin (left) from ear to ear, the mouth clear but its top row. */
const DEAD_FULL = deadBeard(`
  ...........Sm.
  ........SSmm..
  ......SSmmn...
  ....SSmmmn....
  ...Smmmmn.....
  ..Smmmmn......
  ..Smmmmn......
  .Smmmmmmn.....
  .Sm..mmmn.....
  Smm..mSmn.....
  Smm..mSmn.....
  Smm..mmmn.....
  Smm..mSmn.....
  Smm..mSmn.....
  mmm..mmmn.....
  .mm..mmmn.....
  .mmmmmmn......
  ..mmmmmn......
  ..mmmmmn......
  ...mmmmmn.....
  ....nmmmmn....
  ......nnmmn...
  ........nnmn..
  ...........nn.
`);
/** The moustache lying: over the mouth's top row (its right column, lying). */
const DEAD_MOUSTACHE = deadBeard(`
  ........
  ........
  ........
  ........
  ........
  ........
  ........
  ...nmn..
  ....mmn.
  ....Smn.
  ....Smn.
  ....mmn.
  ....Smn.
  ....Smn.
  ....mmn.
  ...nmn..
`);

/**
 * Stubble: a beard's shape on the skin of `face` as a stipple of the hair's deepest tone (so it reads on dark skin too),
 * a quarter of its pixels, half along the jaw's edge. `greying`: salt and pepper, the hair's two lightest tones mixed in
 * an even quarter (under a handlebar, the moustache stays the dark one).
 */
function stipple([c, x, y]: Part, [f, fx, fy]: Part, greying = false): Part {
  const out = new PixelCanvas(c.w, c.h);
  const on = (xx: number, yy: number) => c.get(xx, yy) !== null && f.get(xx + x - fx, yy + y - fy) !== null;
  for (let yy = 0; yy < c.h; yy++) {
    for (let xx = 0; xx < c.w; xx++) {
      if (!on(xx, yy)) continue;
      const edge = !greying && (!on(xx, yy + 1) || !on(xx, yy + 2));
      if (edge ? (xx + yy) % 2 === 0 : (xx + 2 * yy) % 4 === 0)
        out.set(xx, yy, !greying ? 'N' : xx % 8 < 4 ? 'S' : 'm');
    }
  }
  return [out, x, y];
}

/**
 * Facial hair (ADR-0088, ADR-0090), in the hair colour (authored in chestnut), on every face: the first is none. Drawn
 * under the hair's front (a lock falls over a sideburn) and over the face's mouth only on its top row.
 */
export const BEARDS = {
  none: { front: [], dead: [] },
  stubble: { front: [stipple(FULL, FACE)], dead: [stipple(DEAD_FULL, DEAD_FACE)] },
  moustache: {
    front: [
      beard(`
        ....................................
        ....................................
        ....................................
        ....................................
        ....................................
        ....................................
        ....................nmmSSmmn........
        ...................nmmmmmmmmmn......
        ...................mn.......nm......
      `),
    ],
    dead: [DEAD_MOUSTACHE],
  },
  // A thin moustache over the lip in two tones (one reads on any skin) and a short beard on the chin's last row.
  pencil: {
    front: [
      beard(`
        ....................................
        ....................................
        ....................................
        ....................................
        ....................................
        .....................mmm.mmm........
        ....................nNNNNNNNn.......
        ....................................
        ....................................
        ......................n.....n.......
        .....................nmmmmmmmn......
        ......................NnnnnnN.......
      `),
    ],
    dead: [
      deadBeard(`
        ........
        ........
        ........
        ........
        ........
        ........
        ........
        .....n..
        .n...Nm.
        .mn..Nm.
        nm...Nm.
        nm...N..
        nm...Nm.
        nm...Nm.
        nm...Nm.
        Nmn..n..
        .n......
      `),
    ],
  },
  // Thick and rimmed in the deepest tone, sweeping down past the mouth and curling up beside the eyes.
  handlebar: {
    front: [
      // Salt and pepper on the jaw and chin, not up the sideburns.
      clip(stipple(FULL, FACE, true), (_, y) => y >= HEAD_Y + 15),
      beard(`
        ....................................
        ....................................
        ....................................
        ....................................
        ..............N..................N..
        .............Nn......NNNnNNN.....nN.
        .............Nm.....NmmmnmmmN....mN.
        .............NNNNNNnnnNNNNNNNnnNNNN.
        ..............NnmmmnnnN......NnmnN..
        ...............NNNNNN.........NNN...
      `),
    ],
    // Lying, the stubble's deep stipple (the light one read as teeth down the lying chin).
    dead: [
      clip(stipple(DEAD_FULL, DEAD_FACE), (x) => x <= DEAD_AT[0] + 5),
      deadBeard(`
        ........
        ........
        ........
        ....NNN.
        ...NnmnN
        ..Nnn...
        ..Nnm...
        ..Nnnm..
        ...nnnm.
        ...NNnm.
        ....Nnm.
        ....Nnn.
        ....Nnm.
        ....Nnm.
        ....Nnn.
        ....Nn..
        ...Nn...
        ..Nnn...
        ..Nnm...
        ...NnmnN
        ....NNN.
      `),
    ],
  },
  goatee: {
    front: [
      beard(`
        ....................................
        ....................................
        ....................................
        ....................................
        ....................................
        ....................................
        ....................nmmSSmmn........
        ...................nmmmmmmmmmn......
        ...................mn.......nm......
        ....................m.......m.......
        ....................nmmmmmmmmn......
        .....................nmmmmmmn.......
        .......................NnnN.........
      `),
    ],
    dead: [
      DEAD_MOUSTACHE,
      deadBeard(`
        ......
        ......
        ......
        ......
        ......
        ......
        ......
        ..n...
        .nm...
        Smm...
        Smm...
        mmm...
        Smm...
        Smm...
        .nm...
        ..n...
      `),
    ],
  },
  full: { front: [FULL], dead: [DEAD_FULL] },
} satisfies Record<string, Beard>;

interface Extra {
  /** On the face, over the hair (glasses, an earring). */
  face: readonly Part[];
  /** On the torso (a chain). */
  torso: readonly Part[];
  /** On the dead pose. */
  dead: readonly Part[];
}

/** `c` turned a quarter clockwise like the dead pose's head (`turned`), its top-left at (x, y). */
function quarterTurn(c: PixelCanvas, x: number, y: number): Part {
  const out = new PixelCanvas(c.h, c.w);
  for (let cy = 0; cy < c.h; cy++) for (let cx = 0; cx < c.w; cx++) out.set(c.h - 1 - cy, cx, c.get(cx, cy));
  return [out, x, y];
}

/**
 * Round glasses' lenses (at head (14, 7)): bold frames 2 px thick, ink outside and a lit rim inside (slate on top,
 * darker below), a glint in each lens off the eye, the near lens a column wider than the far one (3/4). Drawn 1 px
 * thin they vanished at game zoom; a dark frame reads on every skin, its lit rim on the darkest.
 */
const GLASSES = grid(`
  ..kkkkkk.....kkkkk..
  .k444444k...k44444k.
  k4.....w4kkk4....w4k
  k4....w.4kkk4.....4k
  k4......4k.k4.....4k
  k2......2k.k2.....2k
  k2......2k.k2.....2k
  k2......2k.k2.....2k
  .k222222k...k22222k.
  ..kkkkkk.....kkkkk..
`);

/** Accessories (ADR-0088) in their own colours, for every look: the first is none. */
export const EXTRAS = {
  none: { face: [], torso: [], dead: [] },
  shades: {
    face: [
      [
        grid(`
          ........kkkkkkk...kkkkkkk.
          kkkkkkkkkw5111kkkkkw5111k.
          ........kw1111k...kw1111k.
          ........k51112k...k51112k.
          ........k11122k...k11122k.
          .........k112k.....k112k..
          ..........kkk.......kkk...
        `),
        18,
        32,
      ],
    ],
    torso: [],
    dead: [
      deadBeard(`
        ..............
        .........kkkk.
        ........k5w11k
        ........kw111k
        ........k5112k
        ........k1112k
        ........k1122k
        .........k22k.
        ..........kk..
        ..........k...
        ..........k...
        ..........k...
        ..........k...
        ..........kk..
        .........kkkk.
        ........k5w11k
        ........kw111k
        ........k5112k
        ........k1112k
        ........k1122k
        .........k22k.
        ..........kk..
      `),
    ],
  },
  glasses: {
    face: [
      [
        grid(`
          kkkkkkkk
          22222222
        `),
        18,
        33,
      ],
      [GLASSES, 26, 31],
    ],
    torso: [],
    dead: [quarterTurn(GLASSES, 30, 47)],
  },
  earring: {
    face: [
      [
        grid(`
          ..yy...
          .ywyYN.
          yy..YUN
          Y...YUN
          Y...UUN
          YY.UUN.
          .UUUN..
          ..NN...
        `),
        14,
        38,
      ],
    ],
    torso: [],
    dead: [
      [
        grid(`
          .ywY.
          yy.YU
          Y..UU
          .UUUN
        `),
        42,
        66,
      ],
    ],
  },
  chain: {
    face: [],
    torso: [
      [
        grid(`
          U..................U
          .Y................Y.
          ..UY............YU..
          ....UYUY....YUYU....
          ........UyyU........
          .........YU.........
        `),
        20,
        44,
      ],
    ],
    dead: [
      [
        grid(`
          .....YU
          ....U..
          ...Y...
          ..U....
          ..Y....
          .U.....
          yY.....
          YU.....
          .Y.....
          ..U....
          ..Y....
          ...U...
          ....Y..
          .....UY
        `),
        21,
        50,
      ],
    ],
  },
} satisfies Record<string, Extra>;

export type SkinKey = keyof typeof SKINS;
export type HairKey = keyof typeof HAIRS;
export type HairColourKey = keyof typeof HAIR_COLOURS;
export type EyesKey = keyof typeof EYES;
export type MouthKey = keyof typeof MOUTHS;

/** The hair's darkest tone, for the line where a fringe or lock meets the skin. */
export const HAIR_DEEP: PaletteKey = 'N';
